import { LAUNCHES_PER_GAME, LETTER_SEED_SALT, MAIN_BALL_SEED_SALT, BONUS_BALL_SEED_SALT, levelToMultiplier, BlinkoVersion } from './game-engine/constants';
import { deriveSeed } from './utils';
import { simulateBall } from './game-engine/simulator';

export type SeedPlan = {
  letterSeed: number;
  ballSeeds: number[];
  bonusSeedCounter: number;
};

// LAZY seed plan: generate ONLY the main game (10 balls). Bonus levels are generated on
// demand via generateLevelSeeds when the game advances a level. This avoids precomputing
// the whole ladder up front — at maxLevel 13 the eager plan was ~82k balls/game (each
// physics-simulated), which froze the browser. Deep levels are extremely rare, so on-demand
// per-level generation keeps every game responsive.
export const generateSeedPlan = (version: BlinkoVersion, seed: number): SeedPlan => {
  const letterSeed = deriveSeed(seed, 0, LETTER_SEED_SALT);
  const ballSeeds: number[] = [];
  for (let i = 0; i < LAUNCHES_PER_GAME; i++) {
    const baseSeed = deriveSeed(seed, i + 1, MAIN_BALL_SEED_SALT);
    ballSeeds.push(simulateBall(version, baseSeed, letterSeed).seedUsed);
  }
  return { letterSeed, ballSeeds, bonusSeedCounter: 0 };
};

// Generate the ball seeds for one bonus level (level >= 2), on demand.
// Bonus balls consumed by earlier levels 2..level-1 = LAUNCHES_PER_GAME*(2^(level-1) - 2),
// which is the seed-index offset so the derived seeds match the deterministic full plan.
export const generateLevelSeeds = (
  version: BlinkoVersion,
  seed: number,
  level: number,
  letterSeed: number,
): number[] => {
  if (level < 2) return [];
  const offset = LAUNCHES_PER_GAME * (2 ** (level - 1) - 2);
  const ballCount = LAUNCHES_PER_GAME * levelToMultiplier(level);
  const ballSeeds: number[] = [];
  for (let i = 0; i < ballCount; i++) {
    const bonusSeed = deriveSeed(seed, LAUNCHES_PER_GAME + 1 + offset + i, BONUS_BALL_SEED_SALT);
    ballSeeds.push(simulateBall(version, bonusSeed, letterSeed).seedUsed);
  }
  return ballSeeds;
};
